import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { snapshotsAPI } from '../api/client';
import {
  BarChart3,
  Users,
  TrendingUp,
  Shield,
  MapPin,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import OrgHealthCard from '../components/OrgHealthCard';
import type { AdminActivityResponse, AdminDashboardResponse, ForumSummary } from '../types';

const TIME_RANGES = [3, 6, 12] as const;
type TimeRange = (typeof TIME_RANGES)[number];

type SnapshotTypeFilter = 'member' | 'advisor' | 'leader' | 'nonprofit';

const SNAPSHOT_TYPE_FILTERS: { value: SnapshotTypeFilter | null; label: string }[] = [
  { value: null, label: 'All types' },
  { value: 'member', label: 'Member' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'leader', label: 'Leader' },
  { value: 'nonprofit', label: 'Nonprofit' },
];

const SNAPSHOT_TYPE_NOUN: Record<SnapshotTypeFilter, string> = {
  member: 'members',
  advisor: 'advisors',
  leader: 'leaders',
  nonprofit: 'nonprofit members',
};

type ForumSortKey = 'name' | 'members' | 'completion' | 'delta' | 'health';

const HEALTH_RANK: Record<ForumSummary['health'], number> = {
  needs_attention: 0,
  moderate: 1,
  good: 2,
  no_data: 3,
};

// "Organization / By forum" scope control shared by the two activity charts
// (each card scopes independently, mirroring the health card's toggle).
function ForumScopeToggle({
  forums,
  forumId,
  onChange,
}: {
  forums: { id: number; name: string }[];
  forumId: number | null;
  onChange: (forumId: number | null) => void;
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex rounded-lg border border-gray-200 overflow-hidden" role="group" aria-label="Chart data source">
        <button
          onClick={() => onChange(null)}
          aria-pressed={forumId === null}
          className={`px-3 py-1.5 text-sm font-medium transition ${
            forumId === null ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          Organization
        </button>
        <button
          onClick={() => onChange(forumId ?? forums[0]?.id ?? null)}
          aria-pressed={forumId !== null}
          disabled={forums.length === 0}
          className={`px-3 py-1.5 text-sm font-medium transition disabled:opacity-50 ${
            forumId !== null ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          By forum
        </button>
      </div>
      {forumId !== null && (
        <select
          value={forumId}
          onChange={(e) => onChange(Number(e.target.value))}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-navy/30"
          aria-label="Forum"
        >
          {forums.map((f) => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
      )}
    </div>
  );
}

// Org-wide, deliberately anonymized analytics (the former "Analytics" page).
// Admin-only — this is the entire leadership dashboard (/group) now; the
// per-forum operational view lives on /forum's Health & Goals tab. Contains
// no named-member data; all rollups are aggregated.
export default function OrgAnalyticsPanel() {
  const [data, setData] = useState<AdminDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>(6);
  const [forumSort, setForumSort] = useState<{ key: ForumSortKey; dir: 'asc' | 'desc' }>({
    key: 'health',
    dir: 'asc',
  });
  // Per-card forum scoping for the two activity charts (null = org-wide, which
  // reads straight from the dashboard payload; a forum id triggers a scoped
  // fetch of just these series).
  const [submissionsForumId, setSubmissionsForumId] = useState<number | null>(null);
  const [submissionsActivity, setSubmissionsActivity] = useState<AdminActivityResponse | null>(null);
  const [engagementForumId, setEngagementForumId] = useState<number | null>(null);
  const [engagementActivity, setEngagementActivity] = useState<AdminActivityResponse | null>(null);
  const [snapshotType, setSnapshotType] = useState<SnapshotTypeFilter | null>(null);

  const fetchData = async (months: TimeRange, type: SnapshotTypeFilter | null) => {
    setIsLoading(true);
    try {
      const result = await snapshotsAPI.getAdminDashboard(months, type ?? undefined);
      setData(result);
    } catch (error) {
      console.error('Error fetching admin dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(timeRange, snapshotType);
  }, [timeRange, snapshotType]);

  useEffect(() => {
    if (submissionsForumId === null) {
      setSubmissionsActivity(null);
      return;
    }
    let cancelled = false;
    snapshotsAPI.getAdminActivity(timeRange, submissionsForumId, snapshotType ?? undefined)
      .then((result) => { if (!cancelled) setSubmissionsActivity(result); })
      .catch(() => { if (!cancelled) setSubmissionsActivity(null); });
    return () => { cancelled = true; };
  }, [submissionsForumId, timeRange, snapshotType]);

  useEffect(() => {
    if (engagementForumId === null) {
      setEngagementActivity(null);
      return;
    }
    let cancelled = false;
    snapshotsAPI.getAdminActivity(timeRange, engagementForumId, snapshotType ?? undefined)
      .then((result) => { if (!cancelled) setEngagementActivity(result); })
      .catch(() => { if (!cancelled) setEngagementActivity(null); });
    return () => { cancelled = true; };
  }, [engagementForumId, timeRange, snapshotType]);

  useEffect(() => {
    const ids = new Set((data?.forum_summaries ?? []).map((f) => f.id));
    if (submissionsForumId !== null && !ids.has(submissionsForumId)) {
      setSubmissionsForumId(null);
    }
    if (engagementForumId !== null && !ids.has(engagementForumId)) {
      setEngagementForumId(null);
    }
  }, [data, submissionsForumId, engagementForumId]);

  const handleForumSort = (key: ForumSortKey) => {
    setForumSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  };

  const sortedForums = useMemo<ForumSummary[]>(() => {
    if (!data) return [];
    const { key, dir } = forumSort;
    const sign = dir === 'asc' ? 1 : -1;
    return [...data.forum_summaries].sort((a, b) => {
      switch (key) {
        case 'name': return sign * a.name.localeCompare(b.name);
        case 'members': return sign * (a.member_count - b.member_count);
        case 'completion': return sign * (a.completion_rate - b.completion_rate);
        case 'delta': return sign * (a.avg_delta - b.avg_delta);
        case 'health': return sign * (HEALTH_RANK[a.health] - HEALTH_RANK[b.health]) || a.name.localeCompare(b.name);
      }
    });
  }, [data, forumSort]);

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  const orgStats = data?.org_stats;
  const currentMonthLabel = new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  const snapshotsThisMonth = data?.monthly_completion.find((m) => m.month === currentMonthLabel)?.count ?? 0;

  const forumOptions = (data?.forum_summaries ?? []).map((f) => ({ id: f.id, name: f.name }));
  const forumName = (id: number | null) =>
    forumOptions.find((f) => f.id === id)?.name ?? 'this Forum';
  const submissionsSeries = submissionsForumId
    ? submissionsActivity?.monthly_completion ?? []
    : data?.monthly_completion ?? [];
  const engagementSeries = engagementForumId
    ? engagementActivity?.engagement_metrics ?? []
    : data?.engagement_metrics ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center shrink-0">
          <BarChart3 className="text-gold" size={28} />
        </div>
        <div className="flex-1 min-w-[12rem]">
          <h1 className="text-2xl font-bold text-navy">
            Organization Analytics
          </h1>
          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
            <Shield size={12} className="text-navy shrink-0" />
            Aggregated and anonymized — individual snapshot details and private reflections are never shown.
          </p>
        </div>
        <Link
          to="/admin/members"
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-light transition text-sm font-medium whitespace-nowrap"
        >
          <Users size={16} />
          All Members
        </Link>
        {/* Time range filter */}
        <div className="flex bg-off-white rounded-lg p-0.5">
          {TIME_RANGES.map(r => (
            <button
              key={r}
              onClick={() => setTimeRange(r)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                timeRange === r
                  ? 'bg-white text-navy shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {r}mo
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
          Snapshot type
        </span>
        <div className="flex bg-off-white rounded-lg p-0.5 flex-wrap" role="group" aria-label="Snapshot type">
          {SNAPSHOT_TYPE_FILTERS.map((option) => (
            <button
              key={option.label}
              onClick={() => setSnapshotType(option.value)}
              aria-pressed={snapshotType === option.value}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                snapshotType === option.value
                  ? 'bg-white text-navy shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <Users className="text-navy" size={24} />
          <p className="text-2xl font-bold text-navy mt-2">{orgStats?.total_users ?? 0}</p>
          <p className="text-sm text-gray-500">Total Users</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <MapPin className="text-gold" size={24} />
          <p className="text-2xl font-bold text-navy mt-2">{orgStats?.total_forums ?? 0}</p>
          <p className="text-sm text-gray-500">Forums (Chapters)</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <TrendingUp className="text-green-600" size={24} />
          <p className="text-2xl font-bold text-navy mt-2">{orgStats?.active_users ?? 0}</p>
          <p className="text-sm text-gray-500">Active Users (30d)</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <BarChart3 className="text-navy" size={24} />
          <p className="text-2xl font-bold text-navy mt-2">{snapshotsThisMonth}</p>
          <p className="text-sm text-gray-500">Snapshots This Month</p>
        </div>
      </div>

      {/* Org-wide State/Direction radar + trend (same card as the forum
          dashboard, aggregated across every active user), with an
          Organization / By forum scope. */}
      <OrgHealthCard forums={forumOptions} snapshotType={snapshotType ?? undefined} />

      <div className="grid md:grid-cols-2 gap-6">
        {/* Monthly Completion Trend */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <h2 className="font-semibold text-navy">Monthly Snapshot Submissions</h2>
            <ForumScopeToggle
              forums={forumOptions}
              forumId={submissionsForumId}
              onChange={setSubmissionsForumId}
            />
          </div>
          {submissionsSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={submissionsSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1efe7" />
                <XAxis dataKey="month" stroke="#666" fontSize={12} />
                <YAxis stroke="#666" fontSize={12} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="count" fill="#043370" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">
              No data available yet
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2 bg-off-white rounded-lg px-3 py-2">
            How many monthly Snapshots were completed each month
            {submissionsForumId ? ` in ${forumName(submissionsForumId)}` : ' across the organization'}
            {snapshotType ? ` by ${SNAPSHOT_TYPE_NOUN[snapshotType]}` : ''} —
            a quick read on how consistently members are doing their monthly reflection.
            Taller bars mean more members submitted that month.
          </p>
        </div>

        {/* Engagement Over Time */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <h2 className="font-semibold text-navy">Engagement Rate Over Time</h2>
            <ForumScopeToggle
              forums={forumOptions}
              forumId={engagementForumId}
              onChange={setEngagementForumId}
            />
          </div>
          {engagementSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={engagementSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1efe7" />
                <XAxis dataKey="month" stroke="#666" fontSize={12} />
                <YAxis domain={[0, 100]} stroke="#666" fontSize={12} unit="%" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number | undefined) => [`${value ?? 0}%`, 'Engagement']}
                />
                <Line
                  type="monotone"
                  dataKey="engagement_rate"
                  stroke="#059669"
                  strokeWidth={3}
                  dot={{ fill: '#059669', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">
              No data available yet
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2 bg-off-white rounded-lg px-3 py-2">
            The share of {engagementForumId
              ? `${forumName(engagementForumId)}'s ${snapshotType ? SNAPSHOT_TYPE_NOUN[snapshotType] : 'members'}`
              : snapshotType
                ? `all ${SNAPSHOT_TYPE_NOUN[snapshotType]}`
                : 'all members'}{' '}
            who were active each month — someone counts as active when they submit a Snapshot
            or complete a Grow Goal that month. A rising line means more of the community is
            participating.
          </p>
        </div>
      </div>

      {/* Forum (Chapter) Delta Summaries */}
      {data && data.forum_summaries.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold text-navy">Chapter Health Summary</h2>
              <p className="text-sm text-gray-500 mt-1">Per-Forum snapshot completion and trend direction — click a column to sort</p>
            </div>
            <Link
              to="/forum"
              className="text-sm font-medium text-navy hover:underline whitespace-nowrap"
            >
              Manage forums →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full stack-table">
              <thead className="bg-off-white">
                <tr>
                  {([
                    { key: 'name' as const, label: 'Forum', align: 'text-left' },
                    { key: null, label: 'Location', align: 'text-left' },
                    { key: 'members' as const, label: 'Members', align: 'text-center' },
                    { key: 'completion' as const, label: 'Completion', align: 'text-center' },
                    { key: 'delta' as const, label: 'Delta', align: 'text-center' },
                    { key: 'health' as const, label: 'Health', align: 'text-center' },
                  ]).map((col) => (
                    <th
                      key={col.label}
                      className={`px-6 py-3 ${col.align} text-xs font-medium text-gray-500 uppercase tracking-wider`}
                    >
                      {col.key ? (
                        <button
                          onClick={() => handleForumSort(col.key!)}
                          className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-navy transition"
                        >
                          {col.label}
                          {forumSort.key === col.key && (
                            <span aria-hidden="true">{forumSort.dir === 'asc' ? '▲' : '▼'}</span>
                          )}
                        </button>
                      ) : (
                        col.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedForums.map((forum: ForumSummary) => (
                  <tr key={forum.id} className="hover:bg-off-white/50 transition">
                    <td className="px-6 py-4 text-sm font-medium text-navy">{forum.name}</td>
                    <td data-label="Location" className="px-6 py-4 text-sm text-gray-500">{forum.location}</td>
                    <td data-label="Members" className="px-6 py-4 text-sm text-center text-gray-700">{forum.member_count}</td>
                    <td data-label="Completion" className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              forum.health === 'no_data'
                                ? 'bg-gray-300'
                                : forum.completion_rate >= 70
                                  ? 'bg-green-500'
                                  : forum.completion_rate >= 40
                                    ? 'bg-yellow-500'
                                    : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(forum.completion_rate, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-700">{forum.completion_rate}%</span>
                      </div>
                    </td>
                    <td data-label="Delta" className="px-6 py-4 text-center">
                      {forum.health === 'no_data' ? (
                        <span className="text-sm text-gray-400">—</span>
                      ) : (
                        <span className={`text-sm font-medium ${
                          forum.avg_delta > 0 ? 'text-green-600' : forum.avg_delta < 0 ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {forum.avg_delta > 0 ? '+' : ''}{forum.avg_delta}%
                        </span>
                      )}
                    </td>
                    <td data-label="Health" className="px-6 py-4 text-center">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        forum.health === 'good'
                          ? 'bg-green-100 text-green-700'
                          : forum.health === 'moderate'
                            ? 'bg-yellow-100 text-yellow-700'
                            : forum.health === 'no_data'
                              ? 'bg-gray-100 text-gray-500'
                              : 'bg-red-100 text-red-700'
                      }`}>
                        {forum.health === 'good'
                          ? 'Good'
                          : forum.health === 'moderate'
                            ? 'Moderate'
                            : forum.health === 'no_data'
                              ? 'No snapshots yet'
                              : 'Needs Attention'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
